/**
 * Who a charge's money is for, chosen on the row where the charge is being recorded.
 *
 * ## Why this is a picker with a door in it rather than a picker and a settings screen
 *
 * The agency collects everything a tenant pays and hands each part of it to whoever it belongs to:
 * the rent to the landlord, the caution deposit usually to the landlord, its own fee to itself, the
 * agreement fee to the lawyer who drew it. So a charge is not only an amount and a label — it
 * carries a beneficiary, and the list of them belongs to the agency rather than to this source
 * file, because the third party on a letting is a lawyer at one agency and a surveyor at another.
 *
 * **Adding one happens here, inside the picker, and that is the operator's instruction rather than
 * a convenience.** The moment somebody needs a beneficiary the list does not have is the moment
 * they are half way through recording a charge, with a rent, a term and four other rows already
 * typed. Sending them to a settings screen to come back is how a form gets abandoned, and the
 * abandoned form is the one carrying the money.
 *
 * The way in is the **last option of the select itself** rather than a button beside it. A charge
 * row is already four controls wide on a laptop and two on a phone; a fifth control that is only
 * ever pressed on the rare occasion would take width from the amount on every row that does not
 * need it. Choosing it opens the dialog and leaves the picker exactly as it was, so a person who
 * opens it by accident loses nothing.
 *
 * ## `accrues` is asked, never guessed
 *
 * Whether money collected for somebody accumulates into a balance a payout run pays out is a
 * property of the beneficiary, set when it is added. A landlord accumulates and is paid; a lawyer
 * is *"just a line of statement"* — recorded, with nothing tracked; the agency is itself. Those are
 * three *usual* answers and not three rules, so the dialog offers the usual answer for the kind and
 * lets it be changed: an agency that does hold a lawyer's fees and settles them monthly is
 * describing an ordinary arrangement, and a form that decided on their behalf would be telling them
 * their own books are wrong. See ADR-0131.
 *
 * ## Never shown to anybody outside the agency
 *
 * A beneficiary is a client as often as it is a supplier, and rent belongs to the landlord — so
 * naming one publicly would print a property owner's name beside their own address. Nothing in this
 * file is imported by the listing site, the portal or the pay link, and
 * `tools/verify-beneficiary-privacy.mjs` is what keeps it that way. See ADR-0137.
 */

import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { accruesByDefault, type BeneficiaryKind } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import { resolveParty, searchParties } from "@/lib/pickers";
import { useSession } from "@/lib/session";
import { SearchSelect } from "./search-select";
import { PhoneInput } from "./phone-input";
import { Alert, Button, Checkbox, Field, Input, Modal, Select } from "./ui";

export interface Beneficiary {
  id: string;
  kind: BeneficiaryKind;
  name: string;
  partyId: string | null;
  partyName?: string | null;
  email: string | null;
  phone: string | null;
  accrues: boolean;
  notes: string | null;
  archivedAt: string | null;
}

/**
 * The value the select carries for its own last row.
 *
 * Two underscores and a word nobody would name a beneficiary, because this is compared against the
 * value of a control an operator can type into nowhere. It never leaves this file: `onChange` is
 * called with a real id or with the empty string, and never with this.
 */
const ADD = "__add__";

/**
 * The agency's list of beneficiaries.
 *
 * One query key for the whole application, so a beneficiary added in a dialog on the tenancy form
 * appears in the picker on the unit form behind it without either screen knowing about the other.
 * The archived ones are asked for only where they are managed — a picker offering a beneficiary
 * somebody has retired is a picker that undoes the retiring.
 */
export function useBeneficiaries(options?: { archived?: boolean }) {
  const archived = Boolean(options?.archived);
  return useQuery({
    queryKey: ["beneficiaries", archived],
    queryFn: () => api.get<{ beneficiaries: Beneficiary[] }>("/beneficiaries", { archived: archived ? "true" : undefined }),
  });
}

/** What to call a beneficiary a record names by id alone. */
export function beneficiaryNameFor(list: readonly Beneficiary[] | undefined, id: string | null | undefined): string | null {
  if (!id) return null;
  return list?.find((entry) => entry.id === id)?.name ?? null;
}

export function BeneficiaryPicker({
  id,
  value,
  onChange,
  ariaLabel = "Who this money is for",
  disabled,
}: {
  id?: string;
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const { can } = useSession();
  const { data } = useBeneficiaries();
  const [adding, setAdding] = useState(false);
  const list = data?.beneficiaries ?? [];

  /*
    A permission is filtered out, never padlocked.

    Somebody without `party.edit` could not add a beneficiary yesterday and cannot today, and their
    agency paying us would not change it — which is the test a padlock has to pass. So the way in
    is simply absent for them, and the picker still works for choosing among what exists. ADR-0114.
  */
  const mayAdd = can("party.edit");

  return (
    <>
      <Select
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        value={value ?? ""}
        onChange={(event) => {
          if (event.target.value === ADD) {
            setAdding(true);
            return;
          }
          onChange(event.target.value || null);
        }}
      >
        {/* Not "nobody" and not a blank: a charge whose destination has not been decided is a real
            and ordinary state, and the payout run reports it as unattributed rather than guessing. */}
        <option value="">Not yet decided</option>
        {list.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}
          </option>
        ))}
        {/*
          A value already on the record that is no longer offered — archived after the charge was
          written. It is kept selectable so that opening an old record and saving it does not
          quietly move the money to somebody else.
        */}
        {value && !list.some((entry) => entry.id === value) ? (
          <option value={value}>Previously chosen, no longer on the list</option>
        ) : null}
        {mayAdd ? <option value={ADD}>Add a beneficiary…</option> : null}
      </Select>

      <AddBeneficiaryModal
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={(created) => onChange(created)}
      />
    </>
  );
}

/**
 * Adding one, from wherever the choice is being made.
 *
 * It posts to the same endpoint the management page uses, so nothing is created a second way: the
 * difference is only which fields are asked for, and here it is the fewest that make a usable
 * record. Everything else is on the beneficiaries page, where somebody can go afterwards.
 */
export function AddBeneficiaryModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** The new beneficiary's id, so the picker that opened this can select it. */
  onCreated: (id: string) => void;
}) {
  const [kind, setKind] = useState<Exclude<BeneficiaryKind, "agency">>("contact");
  const [partyId, setPartyId] = useState("");
  /*
    Whether the person has answered the accrues question themselves.

    Until they have, it follows the kind — which is the usual answer and the one that saves the
    press. The moment they touch it, it stops following: somebody who says "we do hold this
    lawyer's fees" and then corrects the name must not find the software has changed its mind back.
  */
  const [accrues, setAccrues] = useState(accruesByDefault("contact"));
  const [accruesTouched, setAccruesTouched] = useState(false);

  const chooseKind = (next: Exclude<BeneficiaryKind, "agency">) => {
    setKind(next);
    if (!accruesTouched) setAccrues(accruesByDefault(next));
  };

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/beneficiaries", input),
    invalidates: [["beneficiaries"]],
    onSuccess: (made: { id?: string } | undefined) => {
      if (made?.id) onCreated(made.id);
      close();
    },
  });

  /** Back to the state it opens in, so the next charge does not inherit the last one's answers. */
  function close() {
    setKind("contact");
    setPartyId("");
    setAccrues(accruesByDefault("contact"));
    setAccruesTouched(false);
    create.reset();
    onClose();
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    /*
      Stopped as well as prevented, and it is the second of the two things that make this dialog
      safe inside another form.

      The portal below takes the dialog out of the DOM tree, which is what stops the browser
      submitting the form it was drawn inside. **React events do not follow the DOM tree**: they
      follow the component tree, portal and all, so this submit still reaches the `onSubmit` of the
      form this picker is written inside. Without this line, adding an entry from the client form
      saved the client too — half filled in, while the person was still looking at the dialog.
    */
    event.stopPropagation();
    const values = formValues(event.currentTarget);
    create.mutate({
      kind,
      // Left out for a client, whose name comes off the client record on the server rather than
      // being copied here — two sources for one name is one too many.
      name: kind === "client" ? undefined : values.beneficiaryName,
      partyId: kind === "client" ? partyId : undefined,
      email: nullIfBlank(values.beneficiaryEmail),
      phone: nullIfBlank(values.beneficiaryPhone),
      accrues,
    });
  };

  /*
    Drawn on the document rather than where it is written, and that is a fix rather than a flourish.

    This dialog is opened from a picker that sits inside somebody else's form — the client form, the
    unit form, the box that marks a payout paid. A `<form>` inside a `<form>` cannot be written in
    markup, but React builds the DOM node by node, so it existed at runtime, and Chrome answered a
    press of the dialog's own button by submitting the outer form: the whole page reloaded, the
    entry was never created, and the half-typed record behind it was gone. It looked exactly like an
    endpoint quietly refusing.

    A portal moves the dialog to the end of `<body>`, so there is no nesting for the browser to
    resolve. It is also where a dialog belongs: it is drawn over everything, and nothing in the
    stacking context it was written inside should be able to clip it.
  */
  return createPortal(
    <Modal open={open} onClose={close} title="Add a beneficiary">
      <form onSubmit={submit} className="space-y-4">
        {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}

        <Field
          label="Who they are"
          htmlFor="beneficiaryKind"
          hint="A client is somebody already on your client list. Anybody else is a contact — a lawyer, a surveyor, an association — and is kept here rather than on that list."
        >
          <Select
            id="beneficiaryKind"
            value={kind}
            onChange={(event) => chooseKind(event.target.value as Exclude<BeneficiaryKind, "agency">)}
          >
            <option value="contact">A contact of their own</option>
            <option value="client">Somebody on the client list</option>
          </Select>
        </Field>

        {kind === "client" ? (
          <Field label="Client" htmlFor="beneficiaryParty" required>
            <SearchSelect
              id="beneficiaryParty"
              value={partyId}
              onChange={setPartyId}
              search={searchParties()}
              resolve={resolveParty}
              placeholder="Search the client list…"
              emptyMessage="No client matched."
            />
          </Field>
        ) : (
          <Field label="Name" htmlFor="beneficiaryName" required error={create.fieldErrors.name}>
            <Input id="beneficiaryName" name="beneficiaryName" required placeholder="Okonkwo & Associates" />
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" htmlFor="beneficiaryEmail" error={create.fieldErrors.email}>
            <Input id="beneficiaryEmail" name="beneficiaryEmail" type="email" placeholder="them@example.com" />
          </Field>
          <Field label="Phone" htmlFor="beneficiaryPhone">
            <PhoneInput id="beneficiaryPhone" name="beneficiaryPhone" />
          </Field>
        </div>

        {/*
          The question the whole design turns on, asked plainly rather than inferred from the name.

          The hint says what each answer means in terms of what happens afterwards, because "does
          this accrue" is accounting vocabulary and "is a payout run going to pay them" is what the
          person choosing actually wants to know.
        */}
        <Checkbox
          id="beneficiaryAccrues"
          name="beneficiaryAccrues"
          label="Money collected for them builds up until a payout run pays it"
          hint="Leave this off where the money is handed over at the time and no balance is kept — a lawyer's agreement fee is usually of that kind. It can be changed later."
          checked={accrues}
          onChange={(event) => {
            setAccrues(event.target.checked);
            setAccruesTouched(true);
          }}
        />

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={create.isPending || (kind === "client" && !partyId)}>
            {create.isPending ? "Adding…" : "Add beneficiary"}
          </Button>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>,
    document.body,
  );
}
